import { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Fish, Home, MapPin, MessageCircle, ShoppingBasket } from "lucide-react";
import PageTransition from "../components/PageTransition";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { trackEvent } from "../utils/analytics";
import { SITE_PAGES } from "../data/site";

const QUICK_LINKS = [
  { to: "/", label: "Home", icon: Home },
  { to: "/menu", label: "Products", icon: ShoppingBasket },
  { to: "/locations", label: "Locations", icon: MapPin },
  { to: "/contact", label: "Contact", icon: MessageCircle },
];

/** Classic edit-distance — small inputs (path segments), no library needed. */
function levenshtein(a: string, b: string): number {
  const dp: number[][] = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[a.length][b.length];
}

function findSuggestion(pathname: string) {
  const attempted = pathname.replace(/^\/|\/$/g, "").replace(/-/g, " ").toLowerCase();
  if (!attempted) return null;

  let best: { page: (typeof SITE_PAGES)[number]; distance: number } | null = null;
  for (const page of SITE_PAGES) {
    // The root path "/" strips to an empty string — without filtering that
    // out, any single-character attempt (e.g. "/x") is trivially
    // edit-distance 1 from "" and always "matches" Home.
    const candidates = [page.path.replace(/^\/|\/$/g, "").replace(/-/g, " "), page.title.toLowerCase(), ...page.keywords].filter(
      Boolean
    );
    for (const candidate of candidates) {
      const distance = levenshtein(attempted, candidate.toLowerCase());
      if (!best || distance < best.distance) best = { page, distance };
    }
  }

  // Only suggest when the attempted path is genuinely close. A flat/floored
  // threshold is too generous for short paths — nearly any 4-5 letter slug
  // ends up within edit-distance 3 of *something* in a small keyword list
  // (e.g. "/shop", "/team", "/api" all falsely matched under floor(3)), so
  // short attempts require a near-exact hit while longer ones get more
  // typo tolerance.
  const threshold = attempted.length < 6 ? 1 : Math.floor(attempted.length * 0.3);
  return best && best.distance <= threshold ? best.page : null;
}

export default function NotFound() {
  useDocumentTitle("Page Not Found");
  const location = useLocation();
  const suggestion = useMemo(() => findSuggestion(location.pathname), [location.pathname]);

  useEffect(() => {
    trackEvent("404_not_found", { path: location.pathname });
  }, [location.pathname]);

  return (
    <PageTransition>
      <section className="flex min-h-screen flex-col items-center justify-center bg-ocean-950 px-6 py-24 text-center text-foam-50">
        <Fish className="h-12 w-12 animate-bob text-teal-300" strokeWidth={1.25} />
        <h1 className="mt-6 font-display text-3xl font-bold sm:text-4xl">This one got away.</h1>
        <p className="mt-3 max-w-sm text-foam-100/70">
          The page you&apos;re looking for has swum off somewhere else.
        </p>

        {suggestion && (
          <Link
            to={suggestion.path}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm text-foam-100/80 ring-1 ring-white/15 transition-colors hover:bg-white/20 hover:text-foam-50"
          >
            Did you mean <span className="font-semibold text-teal-300">{suggestion.title}</span>?
          </Link>
        )}

        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-teal-400 px-7 py-3 text-sm font-semibold text-ocean-950 transition-transform hover:scale-105"
        >
          Back to Home
        </Link>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 border-t border-white/10 pt-8">
          {QUICK_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-foam-100/60 transition-colors hover:bg-white/10 hover:text-foam-50"
            >
              <link.icon className="h-3.5 w-3.5" />
              {link.label}
            </Link>
          ))}
        </div>
      </section>
    </PageTransition>
  );
}
